import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation } from '../../stores/conversationStore';

interface ConversationHistoryProps {
  conversations: Conversation[];
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({ conversations }) => {
  const navigate = useNavigate();

  const handleConversationClick = (conversation: Conversation) => {
    navigate(`/chat/${conversation.gameId}/${conversation.id}`);
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInHours = (now.getTime() - new Date(date).getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return new Date(date).toLocaleDateString();
    }
  };

  if (conversations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Recent Conversations
        </h3>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-gray-400 text-xl">💬</span>
          </div>
          <p className="text-gray-500 text-sm">
            No conversations yet. Start chatting to see your history here!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Recent Conversations
        </h3>
        <button
          onClick={() => navigate('/conversations')}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          View all
        </button>
      </div>
      
      <div className="space-y-3">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            onClick={() => handleConversationClick(conversation)}
            className="p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {conversation.title}
              </h4>
              <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                {formatDate(conversation.lastMessageAt)}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {conversation.gameName}
                </span>
                <span className="text-xs text-gray-500">
                  {conversation.messageCount} messages
                </span>
              </div>
            </div>
            
            {conversation.lastMessage && (
              <p className="text-xs text-gray-600 mt-2 truncate">
                {conversation.lastMessage}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};